import { Loader, Select, Stack, TextInput } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { useDebouncedValue } from '@mantine/hooks'
import { useParams } from '@tanstack/react-router'
import Form from 'common/Form'
import FeatureInput from 'common/inputs/FeatureInput'
import NumberWithUnitInput from 'common/inputs/NumberWithUnitInput'
import { useProductWithVariantsWithoutSuspense, useSearchProductsSymbols } from 'hooks/products'
import { useEffect, useState } from 'react'
import { Category, Unit } from 'types/products'
import { shortageSchema } from 'types/schemas'
import { NewShortage, UpdateShortage } from 'types/shortage'
import { categoriesForSelect, getUnitName, trimFormValues } from 'utils'

type NewProps = {
  onSubmit: (values: NewShortage) => void
  onCancel: () => void
  initialValues?: undefined
}

type UpdateProps = {
  onSubmit: (values: UpdateShortage) => void
  onCancel: () => void
  initialValues: UpdateShortage & { productSymbol: string }
}

type Props = NewProps | UpdateProps

export default function ShortageForm({ onSubmit, onCancel, initialValues }: Props) {
  const productId = useParams({
    from: '/_auth/product/$productId/{-$section}',
    select: (params) => params.productId,
    shouldThrow: false,
  })

  const [searchSymbol, setSearchSymbol] = useState<string>(initialValues ? initialValues.productSymbol : '')
  const [debouncedSearchSymbol] = useDebouncedValue(searchSymbol, 300)

  const products = useSearchProductsSymbols({ symbol: debouncedSearchSymbol })
  const { data } = useProductWithVariantsWithoutSuspense(productId)

  const form = useForm<NewShortage>({
    validate: zodResolver(shortageSchema),
    transformValues: (values) =>
      trimFormValues(values, (trimmedValues) => ({
        ...trimmedValues,
        // if order number starts with "z-", make the "z-" uppercase
        orderNumber: trimmedValues.orderNumber.startsWith('z-')
          ? `Z-${trimmedValues.orderNumber.slice(2)}`
          : trimmedValues.orderNumber,
        feature: trimmedValues.feature || null,
        comments: trimmedValues.comments || null,
      })),
    initialValues: initialValues
      ? {
          productId: initialValues.productId,
          category: initialValues.category,
          orderNumber: initialValues.orderNumber,
          size: initialValues.size,
          feature: initialValues.feature,
          comments: initialValues.comments,
          amount: initialValues.amount,
          unit: initialValues.unit,
        }
      : {
          productId: null!,
          category: null!,
          orderNumber: undefined!,
          size: undefined!,
          feature: undefined!,
          comments: undefined!,
          amount: undefined!,
          unit: Unit.METER,
        },
  })

  const unit = form.getValues().unit

  useEffect(() => {
    if (data) {
      form.setFieldValue('productId', data.id)
    }
  }, [data])

  useEffect(() => {
    if (products.data && initialValues && !form.values.productId) {
      form.setFieldValue('productId', initialValues.productId)
    }
  }, [products])

  useEffect(() => {
    if (form.values.category === Category.FABRIC) {
      form.setFieldValue('unit', Unit.METER)
    } else {
      form.setFieldValue('unit', Unit.PIECE)
    }
  }, [form.values.category])

  return (
    <Form
      onSubmit={(data) => onSubmit({ ...data, id: initialValues ? initialValues.id : '' })}
      form={form}
      onCancel={onCancel}
      submitText={initialValues ? 'Zapisz zmiany' : 'Dodaj brak'}
    >
      <Stack gap="xs">
        <TextInput
          label="Numer zamówienia"
          description="Numer zamówienia, numer telefonu lub inny identyfikator"
          placeholder="123456"
          withAsterisk
          {...form.getInputProps('orderNumber')}
        />
        <Select
          label="Rodzaj produktu"
          placeholder="Wybierz rodzaj produktu"
          data={categoriesForSelect}
          withAsterisk
          comboboxProps={{ shadow: 'md' }}
          {...form.getInputProps('category')}
        />
        <NumberWithUnitInput
          label="Brakująca ilość"
          description={`Ilość brakującego produktu w ${getUnitName(unit).miejscownik}`}
          availableUnits={form.values.category === Category.FABRIC ? [Unit.METER] : [Unit.PIECE]}
          form={form}
          withAsterisk
        />
        <Select
          label="Produkt"
          description="Symbol brakującego produktu. Musi być dostępny w systemie."
          placeholder="Wybierz produkt z listy"
          data={products.data?.map((product) =>
            product.alternateSymbol
              ? { label: `${product.symbol} (${product.alternateSymbol})`, value: product.id }
              : { label: product.symbol, value: product.id }
          )}
          disabled={products.isLoading}
          leftSection={products.isLoading ? <Loader size="xs" /> : undefined}
          value={products.isLoading ? 'Ładowanie dostępnych produktów...' : undefined}
          searchValue={searchSymbol}
          onSearchChange={(value) => setSearchSymbol(value)}
          limit={5}
          searchable
          withAsterisk
          nothingFoundMessage="Nie znaleziono produktu o takim symbolu"
          comboboxProps={{ shadow: 'md' }}
          {...(products.isLoading && !initialValues ? {} : form.getInputProps('productId'))}
        />
        <TextInput
          label="Wymiar"
          description="Wymiar brakującego produktu. Wysokość lub wymiary w cm."
          placeholder="250 cm"
          withAsterisk
          {...form.getInputProps('size')}
          // make this always lowercase and remove spaces
          onChange={(e) => form.setFieldValue('size', e.currentTarget.value.toLowerCase().replace(/\s+/g, ''))}
        />
        <FeatureInput
          description="Cecha brakującego produktu, np. kolor lub podkład. Braki są grupowane po cesze podczas zamawiania."
          form={form}
        />
        <TextInput
          label="Uwagi"
          description="Dodatkowe informacje dotyczące brakującego produktu"
          placeholder="Pilne"
          {...form.getInputProps('comments')}
        />
      </Stack>
    </Form>
  )
}
